import React from "react";

import {
  Box,
  Card,
  CardContent,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { getCardStyles, sectionLabelSx } from "src/shell/styles/themeUtils";

import type { TagSchemaDefinition } from "../../../../organization/types/OrganizationTypes";
import { focusRingSx } from "../utils";

interface FinOpsTagsSectionProps {
  tagSchema: TagSchemaDefinition[];
  tagValues: Record<string, string>;
  setTagValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  isSubmitting: boolean;
  requiredTags: TagSchemaDefinition[];
}

export function FinOpsTagsSection({
  tagSchema,
  tagValues,
  setTagValues,
  isSubmitting,
  requiredTags,
}: FinOpsTagsSectionProps): React.ReactElement {
  const theme = useTheme();

  return (
    <Card sx={getCardStyles(theme)}>
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="subtitle2" sx={sectionLabelSx}>
            FinOps Tags
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Tags enable cost allocation and showback/chargeback reporting.
          {requiredTags.length > 0 &&
            ` ${requiredTags.length} required tag${requiredTags.length > 1 ? "s" : ""}.`}
        </Typography>

        {tagSchema.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tag schema defined for this organization. Tags can be configured
            in organization settings under FinOps.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {tagSchema.map((def) => {
              const value = tagValues[def.key] ?? "";
              const isMissing = def.required && !value.trim();
              return (
                <Box key={def.id}>
                  {def.allowedValues.length > 0 ? (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 1,
                          color: isMissing ? "error.main" : "text.secondary",
                        }}
                      >
                        {def.label}
                        {def.required && " *"}
                      </Typography>
                      <Select
                        fullWidth
                        value={value}
                        onChange={(e) =>
                          setTagValues((prev) => ({
                            ...prev,
                            [def.key]: e.target.value,
                          }))
                        }
                        disabled={isSubmitting}
                        displayEmpty
                        error={isMissing}
                        sx={focusRingSx(theme)}
                        renderValue={(v) =>
                          v || (
                            <span
                              style={{
                                color: theme.palette.text.disabled,
                              }}
                            >
                              Select {def.label}
                            </span>
                          )
                        }
                      >
                        <MenuItem value="">
                          <em>None</em>
                        </MenuItem>
                        {def.allowedValues.map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  ) : (
                    <TextField
                      label={`${def.label}${def.required ? " *" : ""}`}
                      fullWidth
                      value={value}
                      onChange={(e) =>
                        setTagValues((prev) => ({
                          ...prev,
                          [def.key]: e.target.value,
                        }))
                      }
                      disabled={isSubmitting}
                      error={isMissing}
                      helperText={
                        isMissing
                          ? `${def.label} is required for FinOps cost allocation`
                          : undefined
                      }
                      sx={focusRingSx(theme)}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}
